import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import FeatureGrid from '@src/components/DisposalPage/FeatureGrid'
import Approach from '@src/components/DisposalPage/Approach'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import { withCompostingIcons } from '@src/constants/compostingIcons'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import composting from '../../json/commercial-composting-data.json'

const compostingServiceData = {
  name: 'Commercial Composting Services',
  description:
    'Commercial composting collection for multifamily, commercial, and food-service properties — outdoor bins or door-to-door valet service, weekly collection, bins supplied and cleaned, and quarterly diversion reporting.',
  serviceType: 'Commercial Composting',
  url: 'https://getdimension.com/commercial-composting',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Outdoor Composting Bins' },
    { name: 'Valet Composting, Door to Door' },
    { name: 'Valet Composting Plus Trash Collection' },
    { name: 'Restaurant and Food-Service Composting' },
  ],
}

const compostingFAQData = {
  questions: composting.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function CommercialComposting() {
  return (
    <>
      <Head>
        <title>Commercial Composting Services for Businesses | Dimension</title>
        <meta
          name='description'
          content='Commercial composting for multifamily, office, and food-service properties. Weekly collection, bins supplied and cleaned, and quarterly diversion reporting.'
        />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <link rel='canonical' href='https://getdimension.com/commercial-composting' />
        <SchemaMarkup type='Organization' />
        <SchemaMarkup type='WebSite' />
        <SchemaMarkup type='Service' data={compostingServiceData} />
        <SchemaMarkup type='FAQPage' data={compostingFAQData} />
        <SchemaMarkup type='LocalBusiness' />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant='hazardous-waste'
          heroImage='compost'
          heading={composting.pitch.heading}
          material={composting.pitch.material}
          pitchText={composting.pitch.pitchText}
          ctaLink={composting.pitch.ctaLink}
          ctaText={composting.pitch.ctaText}
          ctaHeading={composting.pitch.ctaHeading}
          ctaSubtext={composting.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureGrid {...composting.benefits} list={withCompostingIcons(composting.benefits.list)} />
        <SustainabilityDashboardPromo />
        <FeatureList {...composting.solutions} />
        <FeatureGrid {...composting.whatIs} list={withCompostingIcons(composting.whatIs.list)} />
        <Approach {...composting.approach} />
        <FAQ {...composting.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Commercial Composting in Austin',
              href: '/commercial-composting/austin',
              description:
                'Austin requires composting at multifamily properties with five or more units. What the ordinance asks for, and how a compliant program is built.',
            },
            {
              label: 'Trash Valet',
              href: '/trash-valet',
              description:
                'Doorstep trash and recycling collection for multifamily properties — the amenity our door-to-door composting service runs alongside.',
            },
            {
              label: 'Recurring Waste Services',
              href: '/recurring-waste-services',
              description:
                'Scheduled collection on a set cadence for every other stream your property produces, not just organics.',
            },
            {
              label: 'Commercial Waste Disposal',
              href: '/commercial-waste-disposal',
              description:
                'Multi-site waste accounts for businesses: sector coverage, volume pricing, and one point of contact across markets.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default CommercialComposting
